import { CreatePZDto, PZItem } from '../../types/pz';
import { isAllocationUnit } from '../../utils/commercialQuantity';
import type { AllocationUnit } from '../../utils/dimensionConversion';
import { normalizeUnitCode } from '../../utils/units';
import { isWarehouseQuantity, roundWarehouseQuantity } from '../../utils/warehouseQuantity';

/** Normalizes a nullable/undefined quantity draft value to a payload-ready
 * number|null — empty (null/undefined) round-trips as an explicit `null`
 * ("not entered"), never as `undefined`, so the receive-time fallback
 * contract is unambiguous on the wire. */
function normalizeNullableQuantity(value: number | null | undefined): number | null {
  return value == null ? null : roundWarehouseQuantity(value);
}

type PZItemPayload = CreatePZDto['items'][number];

interface BuildPZItemsPayloadOptions {
  canEditPurchasePrices: boolean;
  documentCurrency: string;
}

export type PZItemDraft = Omit<Partial<PZItem>, 'quantityOrdered' | 'unitPrice'> & {
  // Ant Design InputNumber emits null when the operator clears the field.
  quantityOrdered?: number | null;
  unitPrice?: number | null;
};

export type PZItemValidationKey =
  | 'itemsRequired'
  | 'unitUnsupported'
  | 'quantityRequired'
  | 'quantityPositive'
  | 'priceRequired'
  | 'pricePositive'
  | 'quantityReceivedRange';

const hasProductVariant = (
  item: PZItemDraft
): item is PZItemDraft & { productVariantId: string } =>
  typeof item.productVariantId === 'string' && item.productVariantId.trim().length > 0;

const isMissingNumber = (value: unknown): boolean => (
  value === null || value === undefined || value === ''
);

/**
 * New PZ lines use only the shared dimensional quantity model. Legacy scalar
 * units remain readable on old documents, but editing such a line must never
 * silently relabel its amount as pieces.
 */
const normalizeActivePZUnit = (value: string | null | undefined): AllocationUnit | null => {
  const rawUnit = typeof value === 'string' && value.trim() ? value : 'pcs';
  const normalizedUnit = normalizeUnitCode(rawUnit);
  return isAllocationUnit(normalizedUnit) ? normalizedUnit : null;
};

export function validatePZItemsForSave(
  items: PZItemDraft[],
  options: Pick<BuildPZItemsPayloadOptions, 'canEditPurchasePrices'>,
): PZItemValidationKey | null {
  const selectedItems = items.filter(hasProductVariant);
  if (selectedItems.length === 0) return 'itemsRequired';

  for (const item of selectedItems) {
    if (!normalizeActivePZUnit(item.unit)) {
      return 'unitUnsupported';
    }
    if (isMissingNumber(item.quantityOrdered)) {
      return 'quantityRequired';
    }
    const quantity = Number(item.quantityOrdered);
    if (!isWarehouseQuantity(quantity)) return 'quantityPositive';

    // Empty (null/undefined) is a valid "not entered" state — the backend
    // falls back to the ordered quantity at receive time. Only a value the
    // operator actually typed is checked for physical precision and the
    // non-negative persistence range. Receiving more than ordered is handled
    // by the shared operational-exception confirmation flow.
    if (!isMissingNumber(item.quantityReceived)) {
      const received = Number(item.quantityReceived);
      if (!isWarehouseQuantity(received, 0)) return 'quantityReceivedRange';
    }

    if (options.canEditPurchasePrices) {
      if (isMissingNumber(item.unitPrice)) {
        return 'priceRequired';
      }
      const unitPrice = Number(item.unitPrice);
      if (!Number.isFinite(unitPrice) || unitPrice <= 0) return 'pricePositive';
    }
  }

  return null;
}

/**
 * Receiving a PZ onto stock (DRAFT/CONFIRMED → RECEIVED) creates the FIFO
 * batches, so a line without a real purchase price would create a zero-value
 * batch and a zero COGS downstream — the backend refuses it
 * (`assertPZItemPricesReadyForStatus`). This is the frontend mirror, used both
 * to disable the "save and receive" choice up front and to block the
 * "Przyjmij towar wirtualnie" button, so the two never disagree.
 */
export function hasPZItemsMissingReceivePrice(items: PZItemDraft[]): boolean {
  // Same line set the payload is built from: a blank editor row the operator
  // never filled in is not a priceless line, it is not a line at all.
  const selectedItems = items.filter(hasProductVariant);
  if (selectedItems.length === 0) return true;

  return selectedItems.some((item) => {
    const unitPrice = Number(item.unitPrice);
    return !Number.isFinite(unitPrice) || unitPrice <= 0;
  });
}

export function buildPZItemsPayload(
  items: PZItemDraft[],
  options: BuildPZItemsPayloadOptions
): PZItemPayload[] {
  return items
    .filter(hasProductVariant)
    .map((item) => {
      const unit = normalizeActivePZUnit(item.unit);
      if (!unit) {
        throw new Error('PZ item unit must be pcs, m2 or m3');
      }

      return ({
        id: item.id,
        productVariantId: item.productVariantId,
        unit,
        // Validation owns required/default semantics. Never silently convert a
        // cleared operator field into one received piece.
        quantityOrdered: roundWarehouseQuantity(item.quantityOrdered),
        // Independent of quantityOrdered — carried through faithfully, never
        // derived from or clamped against it here (the operator's raw entry is
        // already range-validated by validatePZItemsForSave above; the backend
        // owns the trace-lock exception for quantityReceived on ZD-traced lines).
        quantityReceived: normalizeNullableQuantity(item.quantityReceived),
        width: item.width ?? null,
        height: item.height ?? null,
        length: item.length ?? null,
        thickness: item.thickness ?? null,
        netWeight: item.netWeight ?? null,
        grossWeight: item.grossWeight ?? null,
        conversionUnit: item.conversionUnit ?? null,
        conversionQuantity: item.conversionQuantity,
        purchaseQuantity: options.canEditPurchasePrices ? item.purchaseQuantity : undefined,
        purchaseUnit: options.canEditPurchasePrices ? item.purchaseUnit : undefined,
        unitPrice: options.canEditPurchasePrices ? Number(item.unitPrice) : 0,
        exchangeRatePLN: options.canEditPurchasePrices ? item.exchangeRatePLN : undefined,
        currency: item.currency || options.documentCurrency,
        ean: item.ean,
        notes: item.notes,
      });
    });
}
